import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationsProvider, UserOrganizationRow } from '../../providers/organizations-provider';
import { OrganizationRights } from '../../providers/organization-rights';
import { OrganizationsNavigationProvider } from '../../providers/navigation-provider';
import { CurrentUserRolesProvider } from '../../providers/current-user-roles-provider';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import { Observable, of, Subscription, switchMap, tap, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MenuItem, ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { catchError, map } from "rxjs/operators";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { FooterComponent } from "../../../common/components/footer-elitment/footer-elitment";
import { NgForOf } from "@angular/common";
import {
  OrganizationsOrganizationMemberCardComponent
} from "../../components/organization-member-card/organization-member-card";

@Component({
  selector: 'projects-organization-members-page',
  providers: [],
  styleUrls: ['./organization-members.scss'],
  standalone: true,
  imports: [
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    FooterComponent,
    NgForOf,
    OrganizationsOrganizationMemberCardComponent
  ],
  templateUrl: './organization-members.html'
})
export class OrganizationsOrganizationMembersPageComponent implements AfterViewInit, OnDestroy {
    public pageTitle: string | null = null;
    public menus: MenuItem[] = [];
    public shortcutMenus: ModuleMenu[] = [];

    public isLoading = true;

    public organization: Organization | null = null;
    public rights: OrganizationRights = new OrganizationRights(this._utils);

    public membersData: UserOrganizationRow[] = [];
    private subscriptions: Subscription = new Subscription();
    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _title: Title,
        private _router: Router,
        private _route: ActivatedRoute,
        private _toaster: MatSnackBar,
        private _dialoger: MatDialog,
        private _changeDetector: ChangeDetectorRef,
        private _translater: TranslateService,
        private _organizations: OrganizationsProvider
    ) {
    }

    public ngAfterViewInit() {
      this.subscriptions.add(
        this.loadOrganization().pipe(
          switchMap(() => this._route.params.pipe(
            switchMap((params: any) => this.loadData(parseInt(params['customerId'], 10))),
            catchError((error: any) => {
              this.onError(error);
              throw error;  // Rethrow after handling to maintain the flow and possibly log at a higher level
            })
          )),
          catchError((error: any) => {
            this.onError(error);
            return [];  // Return an empty array or a suitable fallback as an observable to complete the stream gracefully
          })
        ).subscribe({
          next: () => {
            this.initNav();
            this.isLoading = false;
          },
          error: (error) => console.error('Error in processing:', error)  // This catches any rethrown errors or new ones from initNav
        })
      );
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

  private loadOrganization(): Observable<void> {
    return this._organizationNavigation.getOrganisationFromRoute().pipe(
      switchMap((organization: Organization | null) => {
        if (organization) {
          this.organization = organization;
          return of();
        } else {
          return throwError(() => new Error('Organization not found'));
        }
      }),
      catchError((error: any) => throwError(() => error))
    );
  }

  private loadData(customerId: number): Observable<void> {
    return this._organizations.getOrganizationUsersRows(this.organization!).pipe(
      map((data: UserOrganizationRow[]) => {
        this.membersData = data;
        if (!this.organization?.id) throw new Error('Organization not found');
        this.rights.evaluate({
          organization: this.organization,
          rolesIds: this._currentUserRoles.getRolesIds(this.organization.id),
          user: this.currentUser.user!
        });
      }),
      catchError((error: any) => throwError(() => new Error(error.message)))
    );
  }

    private initNav(): void {
        this.menus = this._organizationNavigation.getMenus('root', {
            organization: this.organization!,
            rights: this.rights
        });
        this.createPageTitle();
        this.breadcrumbProvider.setDefaultCrumbs([]);
    }

    private createPageTitle(){
        this.pageTitle = this._translater.instant('Organization_members');
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private onError(error: any): void {
        this.isLoading = false;
        this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }
}
